import { dayjs } from '@mobi/utils/date'
import { getFromLocalStorage, setInLocalStorage } from '@mobi/utils/storage'
import { keys as analyticsKeys } from '@core/Services/Analytics/keys'
import { trackEvent } from '@core/Services/Analytics/track'

const enum LocalConstants {
  RegisteredTrackingLocalStorageKey = 'banner__viewed_register_tracked',
}

export interface BannerAnalyticsData {
  bannerName: string
  internalTrackingCode?: string
  accountNumber: number | null
  bannerLocation: string
  bannerPosition: number
  clickUrl: string
  endDate: string
}

export const trackBannerClicked = (data?: BannerAnalyticsData) => {
  if (!data) return
  const newData = mapBannerDataToEcommerceItems(data)
  trackEvent(analyticsKeys.featureBannerClicked, { ecommerce: { items: [newData] } })
}

export const trackBannerViewed = (data?: BannerAnalyticsData) => {
  if (!data) return

  const newData = mapBannerDataToEcommerceItems(data)
  const bannerId = data.bannerName
  const registeredTrackingData = getRegisteredViewedBanners()

  if (registeredTrackingData.has(bannerId)) return

  trackEvent(analyticsKeys.featureBannerViewed, { ecommerce: { items: [newData] } })

  const date30DaysFromNow = dayjs().add(30, 'days')
  registeredTrackingData.set(
    bannerId,
    dayjs(data.endDate).isBefore(date30DaysFromNow) ? data.endDate : date30DaysFromNow.toString()
  )
  setRegisteredViewedBanners(registeredTrackingData)
}

// =============
// Local Helpers
// =============

const getRegisteredViewedBanners = (): RegisteredViewBanners => {
  const registeredTrackingData: RegisteredViewBanners = new Map(
    JSON.parse(getFromLocalStorage(LocalConstants.RegisteredTrackingLocalStorageKey) || '[]')
  )
  registeredTrackingData.forEach((endDate, key, map) => {
    if (dayjs(endDate).isBefore(dayjs())) map.delete(key)
  })
  return registeredTrackingData
}

const setRegisteredViewedBanners = (registeredTrackingData: RegisteredViewBanners) => {
  setInLocalStorage(
    LocalConstants.RegisteredTrackingLocalStorageKey,
    JSON.stringify(Array.from(registeredTrackingData.entries()))
  )
}

const mapBannerDataToEcommerceItems = (data: BannerAnalyticsData) => {
  const itemBrand = data.clickUrl.includes('tabtouch-sports')
    ? 'pre'
    : data.clickUrl.includes('races')
      ? 'Racing'
      : undefined
  const itemIdMatch = data.clickUrl.match(/\/event\/(\d+)$/)
  const itemId = itemIdMatch ? itemIdMatch[1] : undefined

  return {
    promotion_name: data.bannerName,
    internalTrackingCode: data.internalTrackingCode,
    item_id: itemId,
    index: data.bannerPosition,
    creative_slot: data.bannerLocation,
    item_brand: itemBrand,
    item_variant: 'Feature Banner',
  }
}

// =====
// Types
// =====

type RegisteredViewBanners = Map<string, string>
